import React from 'react';
import { Card, Descriptions, Tag, Timeline, Image, Empty, Button, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GET_PORTAL_ORDER_DETAIL, GET_PORTAL_ORDER_TRACKING } from 'graphql/queries/order';
import { PortalOrderDetail, PortalOrderAddress, PortalOrderTracking } from 'graphql/types';
import { getExternalStatusLabel } from 'utils/orderStatus';
import { formatUtcToLocal, formatTrackingEventTime } from 'utils/dateTime';
import { formatTrackingLocation } from 'utils/trackingLocation';

type TFunc = (key: string) => string;

/* 数值 + 固定单位拼接（后端已内建单位：重量 kg、尺寸 cm），值为空显示 '-' */
const formatMeasure = (value: number | null | undefined, unit: string): string => {
    if (value === null || value === undefined) return '-';
    return `${value} ${unit}`;
};

/*
 * 地址拼接：streetAddress + addressLine2(公寓号/单元号) + city + stateProvince，", " 分隔。
 * 顺序与后端 PortalOrderAddressDTO 的契约、desktop-web splicingAddress 保持一致——公寓号必须紧跟街道，
 * 不能落到州省后面。desktop-web 还会再拼邮编与国家，客户门户按需求去掉：邮编在本页单独成行，国家恒为 US。
 */
const spliceAddress = (a: PortalOrderAddress | null | undefined): string => {
    if (!a) return '-';
    const parts = [a.streetAddress, a.addressLine2, a.city, a.stateProvince].filter((p) => p && p.trim());
    return parts.length > 0 ? parts.join(', ') : '-';
};

/*
 * 单个地址区块的字段：Name 与 Zipcode 并排一行，Address 独占一行（含门牌/单元号 addressLine2）。
 * 返回 Descriptions.Item 数组而非包裹组件——antd Descriptions 只识别直接子级的 Descriptions.Item，
 * 包一层自定义组件会导致这些 Item 不被渲染。
 */
const addressItems = (
    t: TFunc,
    address: PortalOrderAddress | null,
    prefix: 'sender' | 'receiver'
): React.ReactNode[] => [
    <Descriptions.Item key={`${prefix}-name`} label={t(`orderDetail.fields.${prefix}Name`)}>
        {address?.fullName || '-'}
    </Descriptions.Item>,
    <Descriptions.Item key={`${prefix}-zipcode`} label={t(`orderDetail.fields.${prefix}Zipcode`)}>
        {address?.postalCode || '-'}
    </Descriptions.Item>,
    <Descriptions.Item key={`${prefix}-address`} label={t(`orderDetail.fields.${prefix}Address`)} span={2}>
        {spliceAddress(address)}
    </Descriptions.Item>,
];

/**
 * 带二级标题的地址区块。
 *
 * 发件人、收件人、修改前的收件人三处结构完全一致，统一由本函数渲染，避免标题样式与字段顺序各写一份。
 *
 * @param t 翻译函数
 * @param titleKey 二级标题的 i18n key
 * @param address 地址数据，为空时各字段展示 '-'
 * @param prefix 字段标签前缀，决定用发件人还是收件人的一组文案
 */
const addressBlock = (
    t: TFunc,
    titleKey: string,
    address: PortalOrderAddress | null,
    prefix: 'sender' | 'receiver'
): React.ReactNode => (
    <div>
        <div className="mb-2 text-sm font-medium text-gray-600">{t(titleKey)}</div>
        <Descriptions column={2} bordered size="small">
            {addressItems(t, address, prefix)}
        </Descriptions>
    </div>
);

const OrderDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { trackingNumber } = useParams<{ trackingNumber: string }>();

    const { data, loading } = useQuery<{ portalOrderDetail: PortalOrderDetail }>(GET_PORTAL_ORDER_DETAIL, {
        variables: { trackingNumber },
        skip: !trackingNumber,
    });

    const order = data?.portalOrderDetail;

    /* 轨迹串行加载：详情有结果后才发起，详情为 null（越权/不存在）时不请求轨迹 */
    const {
        data: trackingData,
        loading: trackingLoading,
        error: trackingError,
    } = useQuery<{ portalOrderTracking: PortalOrderTracking | null }>(GET_PORTAL_ORDER_TRACKING, {
        variables: { trackingNumber },
        skip: !order,
    });

    const back = (
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/order-query')}>
            {t('orderDetail.back')}
        </Button>
    );

    if (loading) {
        return (
            <div className="space-y-4">
                {back}
                <div className="flex justify-center py-20">
                    <Spin />
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="space-y-4">
                {back}
                <Card>
                    <Empty description={t('orderDetail.empty.notFound')} />
                </Card>
            </div>
        );
    }

    const pod = order.podInfo;
    const podUrls = pod?.photoUrls || [];
    const hasPod = podUrls.length > 0;
    const events = trackingData?.portalOrderTracking?.events || [];

    return (
        <div className="space-y-4">
            {back}

            <div className="flex flex-col lg:flex-row gap-4 items-start">
                {/* 左列：订单信息 */}
                <div className="w-full lg:flex-1 lg:min-w-0 space-y-4">
                    {/* ① 基础信息 */}
                    <Card title={t('orderDetail.sections.basic')} size="small">
                        <Descriptions column={2} bordered size="small">
                            <Descriptions.Item label={t('orderDetail.fields.trackingNumber')}>
                                {order.trackingNumber || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.channelOrderNumber')}>
                                {order.channelOrderNumber || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.customerReference')}>
                                {order.externalTrackingNumber || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.status')}>
                                {/* 后端无法归并状态时返回 null，此时不套 Tag、直接展示 '-'，与相邻无值字段一致 */}
                                {order.externalStatus ? (
                                    <Tag>{getExternalStatusLabel(order.externalStatus, t)}</Tag>
                                ) : (
                                    '-'
                                )}
                            </Descriptions.Item>
                            {/*
                              * 五个时间字段按创建→取消→揽收→妥投→退回的固定顺序恒定展示，
                              * 无值时 formatUtcToLocal 返回 '-' 占位，保持页面结构稳定不跳动。
                              */}
                            <Descriptions.Item label={t('orderDetail.fields.orderTime')}>
                                {formatUtcToLocal(order.orderTime)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.canceledAt')}>
                                {formatUtcToLocal(order.canceledAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.pickupAt')}>
                                {formatUtcToLocal(order.pickupAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.deliveredAt')}>
                                {formatUtcToLocal(order.deliveredAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.returnedAt')}>
                                {formatUtcToLocal(order.returnedAt)}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* ② 运单数据：每个地址一个带二级标题的区块 */}
                    <Card title={t('orderDetail.sections.origin')} size="small">
                        <div className="space-y-4">
                            {addressBlock(t, 'orderDetail.senderInfo', order.senderAddress, 'sender')}
                            {addressBlock(t, 'orderDetail.receiverInfo', order.recipientAddress, 'receiver')}
                            {/* 收件地址被改过时，上方展示的是当前生效地址，这里补充下单时的原始地址 */}
                            {order.originalRecipientAddress &&
                                addressBlock(
                                    t,
                                    'orderDetail.originalReceiver',
                                    order.originalRecipientAddress,
                                    'receiver'
                                )}
                        </div>
                    </Card>

                    {/* ③ 包裹信息（单位由后端内建：kg / cm） */}
                    <Card title={t('orderDetail.sections.package')} size="small">
                        <Descriptions column={2} bordered size="small">
                            <Descriptions.Item label={t('orderDetail.fields.weight')}>
                                {formatMeasure(order.weightKg, 'kg')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.volumetricWeight')}>
                                {formatMeasure(order.volumetricWeightKg, 'kg')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.length')}>
                                {formatMeasure(order.lengthCm, 'cm')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.width')}>
                                {formatMeasure(order.widthCm, 'cm')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('orderDetail.fields.height')}>
                                {formatMeasure(order.heightCm, 'cm')}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* ④ 派送信息 POD */}
                    <Card title={t('orderDetail.sections.pod')} size="small">
                        {hasPod ? (
                            <Image.PreviewGroup>
                                <div className="flex flex-wrap gap-3">
                                    {podUrls.map((url, idx) => (
                                        <Image
                                            key={idx}
                                            src={url}
                                            width={120}
                                            height={120}
                                            style={{ objectFit: 'cover' }}
                                            fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjYwIiB5PSI2MCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+"
                                        />
                                    ))}
                                </div>
                            </Image.PreviewGroup>
                        ) : (
                            <Empty description={t('orderDetail.empty.pod')} />
                        )}
                    </Card>
                </div>

                {/* 右列：包裹历史（外部轨迹，独立查询串行加载） */}
                <div className="w-full lg:w-2/5 lg:sticky lg:top-4">
                    <Card
                        title={t('orderDetail.sections.history')}
                        size="small"
                        styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' } }}
                    >
                        {trackingLoading ? (
                            <div className="flex justify-center py-10">
                                <Spin />
                            </div>
                        ) : trackingError ? (
                            <Empty description={t('orderDetail.empty.history')} />
                        ) : events.length > 0 ? (
                            <Timeline
                                items={events.map((ev, idx) => {
                                    /* 位置由 location/城市/州省/邮编/国家码拼成，全空时不渲染该行 */
                                    const locationText = formatTrackingLocation(ev);
                                    return {
                                        key: idx,
                                        children: (
                                            <div>
                                                {/* 后端返回操作地本地时间 + 时区偏移，换算为浏览器本地时区后展示 */}
                                                <div className="text-sm text-gray-500">
                                                    {formatTrackingEventTime(ev.localTime, ev.localGmtOffset)}
                                                </div>
                                                <div className="font-medium">{ev.description || ev.event || '-'}</div>
                                                {locationText && (
                                                    <div className="text-xs text-gray-400">{locationText}</div>
                                                )}
                                            </div>
                                        ),
                                    };
                                })}
                            />
                        ) : (
                            <Empty description={t('orderDetail.empty.history')} />
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailPage;
